import {
    buildUri,
    componentExists,
    getContainer,
    setIsLoading,
    hideElement,
    isDefinedAndNotNull,
    registerComponent,
    isCharSetUTF8,
} from "./utils/dom";

import { addSessionStorageItem, getSessionStorageItem, getSiteCookie, readCookie } from "./utils/storage";
import { AppointmentCategoriesService } from "./services/appointment-categories/appointment-categories.service";
import { AppointmentService } from "./services/appointment/appointment.service";
import { AppointmentTypeService } from "./services/appointment-type/appointment-type.service";
import { AvailabilitySlotService } from "./services/availability/availability-slot.service";
import { ConfigurationService } from "./services/configuration/configuration.service";
import {
    DICT_DEFAULT_RESOURCE_TEXT,
    MODAL_STEPS,
    HIERARCHY_EVENTS,
    APPOINTMENT_TYPE_SELECTION_EVENTS,
    LOCATION_EVENTS,
    TIMESLOT_EVENTS,
    NAVIGATION_EVENTS,
    APPOINTMENT_FORM_SUBMIT_ERRORS,
    EventTypes, EventValues, SCHEDULER, ModalStepsLabel
} from "./utils/constants";
import { generateUUID } from "./utils/guid";
import { LocationService } from "./services/location/location.service";
import { ResourcesService } from "./services/resources/resources.service";
import { SchedulerContainerComponent } from "./components/scheduler-container.component";
import { ThemeService } from "./services/theme/theme.service";
import AppointmentSelectionComponent from "./components/appointment-selection.component";
import AppointmentTimeComponent from "./components/appointment-time.component";
import AppointmentTypeComponent from "./components/appointment-type.component";
import CalendarComponent from "./components/calendar.component";
import CategorySectionComponent from "./components/category-section.component";
import ConfirmationComponent from "./components/confirmation.component";
import DropdownComponent from "./components/dropdown/dropdown.component";
import HierarchyAppointmentComponent from "./components/hierarchy-appointments.component";
import LocationComponent from "./components/location.component";
import Options from "./model/options";
import TimeslotComponent from "./components/timeslot.component";
import SectionComponent from "./components/section.component";
import HierarchyCategories from "./model/hierarchy-data";
import FormComponent from "./components/form.component";
import RegistrationComponent from "./components/registration.component";
import { formatAcknowledgmentDate, formatTimeSlot } from "./utils/date";
import NavigationComponent from "./components/navigation.component";
import ValidationErrorComponent from "./components/validation-error.component";
import { getEmailField, getFieldByMappedField, getTelField } from "./utils/form";
import ButtonComponent from "./components/button.component";
import BreadcrumbComponent from "./components/breadcrumb.component";
import { AnalyticsService } from "./services/analytics/analytics.service";
import {ViewportUtil} from "./utils/viewport";

const v24SelfScheduler = (function () {

    const applicationId = 9;

    const analyticsService  = new AnalyticsService();
    const configService = new ConfigurationService();
    const aptService = new AppointmentService();
    const aptTypeService = new AppointmentTypeService();
    const resourcesService = new ResourcesService();
    const locationService = new LocationService();
    const themeService = new ThemeService();
    const aptCatService = new AppointmentCategoriesService();
    const availabilitySlotService = new AvailabilitySlotService();
    const viewportUtils = new ViewportUtil();

    let isInit = false;
    let container = null;
    let presetOptions = null;
    let selectedCategory = null;
    let selectedAppointmentType = null;
    let history = [];
    let appointment = {};
    let steps = {};
    let currentStep = null;
    let currentStepIndex = 1;
    let stepCount = 1;

    let userguid = null;
    let sessionIdentifier = null;
    let panelIdentifier = null;

    let aptLocationComponent = null;
    let aptValidationErrorComponent = null;
    let aptSelectionComponent = null;
    let aptTimeComponent = null;
    let registrationComponent = null;
    let confirmationComponent = null;

    function resetProperties() {
        stepCount = 1;
        steps = {};
        appointment = {};
        history = [];
        presetOptions = null;
        selectedAppointmentType = null;
        currentStepIndex = 1;
        currentStep = null;

        if (window.v24SchedulerCalendar) {
            window.v24SchedulerCalendar.reset();
        }
    }

    function load(options) {

        if (isInit) {
            console.warn("already initialised");
            return;
        }

        isInit = true;

        resetProperties();

        if (!!window.vee24ServiceConfig) { // Get from Disco.
            v24Scheduler.serviceRegion = !!window.vee24ServiceConfig.serviceRegion ? window.vee24ServiceConfig.serviceRegion : v24Scheduler.serviceRegion;
            v24Scheduler.staticDomain = !!window.vee24ServiceConfig.staticDomain ? window.vee24ServiceConfig.staticDomain : v24Scheduler.staticDomain;
            v24Scheduler.restApi = !!window.vee24ServiceConfig.restApi ? window.vee24ServiceConfig.restApi : v24Scheduler.restApi;
        }

        if (options) {
            presetOptions = new Options(options);
        }

        if (typeof (v24ClientSchedulerInitialise) === 'function') {
            v24ClientSchedulerInitialise();
        }

        container = initComponent();

        if (!container) {

            container = document.body.insertBefore(new SchedulerContainerComponent(), document.body.firstChild);
            container
                .load()
                .catch(err => {
                    console.error(`Unable to load scheduler. Error: ${err}`);
                    removeContainer();
                });


        }
        panelIdentifier = generateUUID();
        analyticsService.loadAnalytics(window.v24Analytics);

        const requestUrl = (window.vee24EngagementPage ? new URLSearchParams(location.href).get('page') : null) || location.href;
        configService.getConfig({ sitename: v24Scheduler.site, siteSection: v24Scheduler.siteSection, culture: v24Scheduler.culture, page: requestUrl }).then(async () => {

            await resourcesService.loadResources({ sitename: configService.sitename, culture: configService.culture, applicationId: applicationId });
            await locationService.getLocations(configService.sitename);

            viewportUtils.init(resourcesService);

            showSchedulerModal();
            container.querySelector(`#v24SchedulerCloseButton`)?.addEventListener("click", hideSchedulerModal);

            themeService.init(resourcesService, container.style);
            container.loadCustomStyles({
                customFontPath: themeService.customFontPath,
            });
            analyticsService.sessionGUID = vee24?.sessionguid;
            initLoadedAppointmentProperties();
            analyticsService.send(EventTypes.PanelStateChange, EventValues.Show, null);
        }).catch(err => {

            console.error(err);
            setIsLoading(false);
            isInit = false;
            //Cant get translation since initial config call has failed.  Should provide a local translation JSON for main cultures?
            container.setContent(`<div class="v24-error">Unable to load</div>`);
        });

    }

    function setupSteps() {

        const showNext = () => currentStep?.getNextStep !== null;

        const canGoBack = () => {

            if (currentStepIndex === MODAL_STEPS.appointmentConfirmationStep) {
                return false;
            }

            return history.length !== 0;
        };

        steps[MODAL_STEPS.appointmentSelectionStep] = {
            getNextStep: async () => {

                appointment.locationId = null;

                let appointmentTypes = aptTypeService.appointmentTypes;

                if (presetOptions?.locationId) {
                    appointmentTypes = AppointmentTypeService.FilterAppointmentsByLocation(aptTypeService.appointmentTypes, presetOptions?.locationId);
                }

                selectedAppointmentType = AppointmentTypeService.FindAppointmentTypeById(appointmentTypes, appointment.appointmentTypeId);

                if (selectedAppointmentType) {


                    const locations = locationService.locations.filter((location) => selectedAppointmentType?.locationIds.includes(location.id));
                    const hasPreSelectedLocation = handlePreSelectedLocation();

                    if (hasPreSelectedLocation || !renderLocations(locations)) {
                        stepCount++;

                        renderTimeComponent();
                        return MODAL_STEPS.appointmentTimeStep;
                    }

                    return MODAL_STEPS.appointmentLocationStep;
                }

                return MODAL_STEPS.appointmentSelectionStep;
            },
            showBack: () => false,
            showNext,
            canGoBack,
            canGoNext: () => appointment?.appointmentTypeId,
            get: () => container.querySelector("vee24-scheduler-appointment-selection-section"),
            getTitle: () => configService.showHierarchicalTreeView ? resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_CATEGORY", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_CATEGORY) : resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_APPOINTMENT_TYPE", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_APPOINTMENT_TYPE),
        };

        steps[MODAL_STEPS.appointmentLocationStep] = {
            getNextStep: async () => {
                renderTimeComponent();
                return MODAL_STEPS.appointmentTimeStep;
            },
            showBack: () => !presetOptions?.hasPresetAppointmentType && !presetOptions?.hasPresetCategory,
            showNext,
            canGoBack,
            canGoNext: () => appointment?.locationId,
            get: () => container.querySelector("vee24-scheduler-appointment-location-section"),
            getTitle: () => resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_LOCATION", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_LOCATION),
        };

        steps[MODAL_STEPS.appointmentTimeStep] = {
            getNextStep: async () => {
                submitTimeSlot();
                return MODAL_STEPS.appointmentRegistrationStep;
            },
            showBack: () => history.length > 0,
            showNext,
            canGoBack,
            canGoNext: () => appointment?.appointmentTime,
            get: () => container.querySelector("vee24-scheduler-appointment-time-section"),
            getTitle: () => resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_CALENDAR", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_CALENDAR),
        };

        steps[MODAL_STEPS.appointmentRegistrationStep] = {
            getNextStep: async () => {
                const response = await submitAppointmentForm();
                if (response > -1) {
                    showAcknowledgement(response);
                    return MODAL_STEPS.appointmentConfirmationStep;
                }
                return MODAL_STEPS.appointmentRegistrationStep;
            },
            showBack: () => true,
            showNext,
            canGoBack,
            canGoNext: () => currentStep.get().formReady,
            get: () => container.querySelector("vee24-scheduler-appointment-registration-section"),
            getTitle: () => resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_REGISTRATION", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_REGISTRATION),
        };

        steps[MODAL_STEPS.appointmentConfirmationStep] = {
            getNextStep: null,
            showBack: () => false,
            showNext,
            canGoBack,
            canGoNext: () => false,
            get: () => container.querySelector("vee24-scheduler-appointment-confirmation-section"),
            getTitle: () => resourcesService.getResource("SELFSCHEDULER_STEP_TITLE_CONFIRMATION", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_STEP_TITLE_CONFIRMATION),
        };

        currentStep = steps[currentStepIndex];
    }

    function updateBreadcrumb(title) {

        const breadcrumbRef = breadcrumb();
        if (breadcrumbRef) {

            let currentStepIndex = history.length + 1;

            if (presetOptions?.locationId > -1) {
                currentStepIndex++;
            }
            if (presetOptions?.hasPresetAppointmentType || presetOptions?.hasPresetCategory) {
                currentStepIndex++;
            }

            breadcrumbRef.update(currentStepIndex, getTotalSteps(), title)
        }
    }

    function getTotalSteps() {

        const totalSteps = Object.values(steps).length - 1; //exclude confirmation step
        const locations = locationService.locations.filter((location) => selectedAppointmentType?.locationIds.includes(location.id));

        if (locations.length === 0 || presetOptions?.locationId === -1) {
            return totalSteps - 1; //exclude location step
        }

        return totalSteps;
    }

    function changeStep(nextStep) {
        const container = getContainer();
        const allSteps = container.querySelectorAll(`[data-step]`);
        Array.from(allSteps).forEach(step => hideElement(step, true));

        currentStepIndex = nextStep;
        currentStep = steps[currentStepIndex];

        //user view event here
        if (currentStep) {

            hideElement(currentStep.get(), false);
            updateBreadcrumb(currentStep.getTitle());
            updateNavigationButtons();
            breadcrumb()?.scrollIntoView() || currentStep.get()?.children[0]?.scrollIntoView();
            setTimeout(() => {
                currentStep.get()?.focus();
            }, 300)

            analyticsService.send(EventTypes.PanelView, currentStep.getTitle(), getCurrPanel());
        }
    }

    async function goToNextStep({trackHistory}={}) {

        if (!currentStep) {
            console.error("current step not defined");
            return;
        }

        const nextStep = await currentStep.getNextStep();

        if (nextStep === currentStepIndex) {
            console.warn("unable to validate response");
            return;
        }

        if (trackHistory) {
            history.push(currentStepIndex);
        }

        stepCount++;

        changeStep(nextStep);
    }

    function initComponent() {
        // If component has already been registered then it has been loaded previously
        // as such, bypass initial load logic
        if (!componentExists("vee24-scheduler-container")) {

            registerComponent("vee24-scheduler-container", SchedulerContainerComponent);
            registerComponent("vee24-scheduler-section", SectionComponent);
            registerComponent("vee24-scheduler-hierarchy-appointments", HierarchyAppointmentComponent);
            registerComponent("vee24-scheduler-dropdown", DropdownComponent);
            registerComponent("vee24-scheduler-category-section", CategorySectionComponent);
            registerComponent("vee24-scheduler-appointment-type-section", AppointmentTypeComponent);
            registerComponent("vee24-scheduler-appointment-form-section", FormComponent);
            registerComponent("vee24-scheduler-appointment-form-error-section", ValidationErrorComponent);
            registerComponent("vee24-scheduler-appointment-timeslot-section", TimeslotComponent);
            registerComponent("vee24-scheduler-appointment-calendar-section", CalendarComponent);
            registerComponent("vee24-scheduler-button", ButtonComponent);

            //Page Specific Components
            registerComponent("vee24-scheduler-appointment-selection-section", AppointmentSelectionComponent);
            registerComponent("vee24-scheduler-appointment-location-section", LocationComponent);
            registerComponent("vee24-scheduler-appointment-registration-section", RegistrationComponent);
            registerComponent("vee24-scheduler-appointment-time-section", AppointmentTimeComponent);
            registerComponent("vee24-scheduler-appointment-confirmation-section", ConfirmationComponent);

            registerComponent("vee24-scheduler-navigation", NavigationComponent);
            registerComponent("vee24-scheduler-breadcrumb", BreadcrumbComponent);
        }

        return getContainer();
    }

    function showSchedulerModal() {
        setTimeout(() => {
            viewportUtils.update();
        },0)

        document.documentElement.classList.add("v24-scheduler-open");
        container.showModal(true);
    }

    function initLoadedAppointmentProperties() {

        const container = getContainer();
        const contentDiv = container.querySelector("#v24SchedulerContent");

        if(contentDiv) {
            const showFullscreenModal = resourcesService.getResource("SELFSCHEDULER_USE_FULLSCREEN_MODAL", "1") === "1";
            if (!showFullscreenModal) {
                document.documentElement.classList.add("v24-scheduler-side-panel");
                contentDiv.classList.add("v24-scheduler-side-panel");
            }
        }

        setIsLoading(false);

        try {

            aptTypeService.getAppointmentTypes(configService.sitename).then(() => {

                if (resourcesService.getResource("SELFSCHEDULER_USE_BREADCRUMB", "0") === "1") {
                    container.addBreadcrumb();
                }

                setupSteps();
                setupIdentifiers();
                if (!setupHeaderTitle()) {
                    setupLogo();
                }
                setupSections();
                setupFooter();

                navigation()?.init(resourcesService);
                navigation()?.addEventListener(NAVIGATION_EVENTS.goBack, () => goBack());
                navigation()?.addEventListener(NAVIGATION_EVENTS.goNext, () => goNext());

                if (presetOptions?.hasPresetAppointmentType && !configService.showHierarchicalTreeView) {

                    selectedAppointmentType = AppointmentTypeService.FindAppointmentTypeById(aptTypeService.appointmentTypes, presetOptions?.appointmentTypeId);

                    if (!selectedAppointmentType) {
                        selectedAppointmentType = AppointmentTypeService.FindAppointmentTypeByName(aptTypeService.appointmentTypes, presetOptions?.appointmentType);
                    }

                    if (!selectedAppointmentType) {
                        presetOptions.appointmentTypeId = presetOptions.appointmentType = null;
                        console.warn("no appointment types found for preset option, show all");
                    }
                    else {
                        appointment.appointmentTypeId = selectedAppointmentType.id;
                        goToNextStep();
                        return;
                    }
                }
                if (aptTypeService.appointmentTypes.length == 1 && !configService.showHierarchicalTreeView) {
                    appointment.appointmentTypeId = aptTypeService.appointmentTypes[0].id;
                    goToNextStep();
                    return;
                }

                changeStep(1);
                const logo = document.getElementById('v24-scheduler-footerlogo');
                logo?.addEventListener('click', () => analyticsService.send(EventTypes.Click, EventValues.PoweredByVee24,getCurrPanel())
                )
            });
        }
        catch(err) {
            console.error(err);
            container.setContent(
              `<div class="v24-error">${resourcesService.getResource(
                "SELFSCHEDULER_APPOINTMENT_TYPES_LOAD_ERROR",
                DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_APPOINTMENT_TYPES_LOAD_ERROR
              )}</div>`
            );
        }
    }

    function setupIdentifiers() {
        userguid = readCookie(getSiteCookie("v24u", configService.sitename));

        const sessionIdentifierFromStorage = getSessionStorageItem("sessionIdentifier");
        if (sessionIdentifierFromStorage === null) {
            sessionIdentifier = generateUUID();
            addSessionStorageItem("sessionIdentifier", sessionIdentifier);
        } else {
            sessionIdentifier = sessionIdentifierFromStorage;
        }
    }

    function setupHeaderTitle() {
        const container = getContainer();
        if (container) {
            const panelTitle = resourcesService.getResource("SELFSCHEDULER_PANEL_TITLE", "");

            if (panelTitle?.length > 0) {
                container.logoContainer.innerHTML = `<h1>${panelTitle}</h1>`;
                return true;
            }
        }
        return false;
    }

    function setupLogo() {
        const container = getContainer();
        if (container) {
            container.logoContainer.innerHTML = `<img id="v24SchedulerSiteImg" src="${buildUri(v24Scheduler.staticDomain, '/v24/veedesk/themes/' + configService.sitename + '/images/scheduler-logo.png')}" alt="${configService.sitename}-scheduler-logo" />`;
        }
    }

    function handleLocationSelected(selectedLocation) {
        if (selectedLocation) {
            appointment.locationId = selectedLocation.id;
            aptLocationComponent.createAddressDetails(selectedLocation);
            updateNavigationButtons();
        }
    }

    function setupSections() {
        aptLocationComponent = container.querySelector("vee24-scheduler-appointment-location-section");
        aptLocationComponent.init(resourcesService);
        aptLocationComponent.addEventListener(LOCATION_EVENTS.locationSelected, (e) => handleLocationSelected(e.detail?.location));

        aptTimeComponent = container.querySelector("vee24-scheduler-appointment-time-section");
        aptTimeComponent.addEventListener(TIMESLOT_EVENTS.timeslotSelected, (e) => {

            if (e.detail.appointmentTime) {

                appointment.appointmentTime = e.detail.appointmentTime;
                updateNavigationButtons();

                setTimeout(() => {
                    navigation().focusNext();
                }, 300);

            }
        });

        aptSelectionComponent = container.querySelector("vee24-scheduler-appointment-selection-section");
        aptSelectionComponent.init(resourcesService);

        const headerResource = resourcesService.getResource("SELFSCHEDULER_APPOINTMENTTYPE_HEADER", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_APPOINTMENTTYPE_HEADER);
        const introductionEl = `<p aria-label="${headerResource}" >${headerResource}</p>`;

        aptSelectionComponent.setInstructions(introductionEl);

        if (configService.showHierarchicalTreeView) {
            aptCatService.loadCategories({
                sitename: configService.sitename,
                locationId: presetOptions?.locationId
            }).then(() => {

                aptSelectionComponent.addEventListener(
                    HIERARCHY_EVENTS.categoryNotFound,
                    (e) => {

                        if (presetOptions) {
                            presetOptions.categoryId = presetOptions.categoryName = null;
                        }
                    }
                );
                aptSelectionComponent.addEventListener(
                    HIERARCHY_EVENTS.categorySelected,
                    (e) => {

                        selectCategory(e.detail.category);

                        if (e.detail?.preSelectedCategory) {
                            goToNextStep();
                        }
                    }
                );

                aptSelectionComponent.addEventListener(
                    HIERARCHY_EVENTS.categoryDeSelected,
                    (e) => {
                        selectCategory(null);
                    }
                );

                const data = new HierarchyCategories(aptCatService.categories);
                aptSelectionComponent.setupCategories(data, presetOptions, resourcesService);

            });

        } else {

            let appointmentTypes = aptTypeService.appointmentTypes;

            if (presetOptions?.locationId) {
                appointmentTypes = AppointmentTypeService.FilterAppointmentsByLocation(aptTypeService.appointmentTypes, presetOptions?.locationId);
            }

            aptSelectionComponent.setupAppointmentList(appointmentTypes);
            aptSelectionComponent.addEventListener(
                APPOINTMENT_TYPE_SELECTION_EVENTS.appointmentTypeSelected,
                (e) => {
                    selectAppointmentType(e.detail.appointmentTypeId);
                    setTimeout(() => {
                        navigation()?.focusNext();
                    }, 300);
                }
            );
        }
    }
    function setupFooter() {
        const privacyPolicyText = resourcesService.getResource('SELFSCHEDULER_PRIVACYPOLICYTEXT', DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_PRIVACYPOLICYTEXT);
        const privacyPolicyLink = resourcesService.getResource('SELFSCHEDULER_PRIVACYPOLICYLINK', '');

        let privacyPolicyHtml = '';

        if (privacyPolicyLink?.length > 0) {
            privacyPolicyHtml = `<a target="_blank" rel="noopener noreferrer" href="${privacyPolicyLink}" title="Privacy Policy" aria-label="Privacy Policy">${privacyPolicyText}</a>`;
        }

        const footerPoweredByText = resourcesService.getResource('SELFSCHEDULER_POWEREDBYTEXT', DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_POWEREDBYTEXT);
        const footerLogoImg = resourcesService.getResource("SELFSCHEDULER_FOOTER_LOGO", buildUri(v24Scheduler.staticDomain, DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_FOOTER_LOGO));
        const footerLogo = container.querySelector("#v24-scheduler-modal-footer");

        if (footerLogo) {
            footerLogo.innerHTML = `
                <span>${privacyPolicyHtml}</span>
                <span>
                    <span>${footerPoweredByText}</span>
                    <a target="_blank" href="https://vee24.com" class="v24-scheduler-footerlogo" title="Vee24 logo" aria-label="Vee24 logo" id="v24-scheduler-footerlogo" rel="noopener noreferrer">
                        <img class="v24logo" src="${footerLogoImg}" alt="Vee24 Logo"/>
                    </a>
                </span>`;
        }
   }

    function updateNavigationButtons() {

        navigation()?.showNext(currentStep.showNext())
            .enableNext(currentStep.canGoNext())
            .showBack(currentStep.showBack())
            .enableBack(currentStep.canGoBack())
            .renderLabelsForStep(currentStepIndex);
    }

    function goBack() {
        analyticsService.send(EventTypes.Click, EventValues.Back, getCurrPanel());

        stepCount--;
        changeStep(history.pop());
    }

    function goNext() {

        if (!currentStep.getNextStep) {
            console.warn("no next step defined");
            return;
        }
        analyticsService.send(EventTypes.Click, EventValues.Next, getCurrPanel());

        goToNextStep({trackHistory: true});
    }

    function breadcrumb() {
        return container.querySelector("vee24-scheduler-breadcrumb");
    }

    function navigation() {
        return container.querySelector("vee24-scheduler-navigation");
    }

    function getCurrPanel() {
        switch (currentStepIndex) {
            case MODAL_STEPS.appointmentSelectionStep:
                return ModalStepsLabel.appointmentSelectionStep;
            case MODAL_STEPS.appointmentLocationStep:
                return ModalStepsLabel.appointmentLocationStep;
            case MODAL_STEPS.appointmentTimeStep:
                return ModalStepsLabel.appointmentTimeStep;
            case MODAL_STEPS.appointmentRegistrationStep:
                return ModalStepsLabel.appointmentRegistrationStep;
            case MODAL_STEPS.appointmentConfirmationStep:
                return ModalStepsLabel.appointmentConfirmationStep;
        }

        return null;
    }

    function hideSchedulerModal() {

        document.documentElement.classList.remove("v24-scheduler-open");
        container.showModal(false);
        viewportUtils.reset();

        analyticsService.send(EventTypes.Close, EventValues.Close, getCurrPanel());

        isInit = false;

    }

    function resetAppointmentTimeSlotSelectionPage() {
        appointment.appointmentTime = null;
        appointment.appointmentDate = null;
        aptTimeComponent?.resetTimeslot();

        updateNavigationButtons();
    }

    function selectCategory(category) {
        if (category) {
            selectAppointmentType(category.appointmentTypeId);
            appointment.categoryId = category.id;
            selectedCategory = category;
        } else {
            selectedCategory = null;
            appointment.categoryId = null;
            appointment.appointmentTypeId = null;
        }

        updateNavigationButtons();
    }

    function selectAppointmentType(appointmentTypeId) {
        selectedAppointmentType = null;
        appointment.appointmentTypeId = appointmentTypeId;

        updateNavigationButtons();
    }

    function removeContainer() {
        container?.remove();
        container = null;
    }

    function renderTimeComponent() {

        resetAppointmentTimeSlotSelectionPage();

        let titleText = resourcesService.getResource(
            "SELFSCHEDULER_CALENDARPAGETITLE",
            DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_CALENDARPAGETITLE
        );
        
        const selectedName = selectedCategory ? selectedCategory.name : selectedAppointmentType?.name;            
        titleText = titleText.replace("{0}", resourcesService.getFormattedResource(selectedName, 'SELFSCHEDULER_APPOINTMENT_TYPE', '', selectedName));

        aptTimeComponent.init(resourcesService, analyticsService);

        let instructionsText = resourcesService.getResource(
            "SELFSCHEDULER_CALENDARPAGEINSTRUCTIONS",
            DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_CALENDARPAGEINSTRUCTIONS
        );

        const location = locationService.findLocationById(appointment.locationId);

        if (location) {

            instructionsText = resourcesService.getResource(
                "SELFSCHEDULER_CALENDARPAGEINSTRUCTIONS_WITH_LOCATION",
                DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_CALENDARPAGEINSTRUCTIONS_WITH_LOCATION
            );

            instructionsText = instructionsText.replace("{0}", location.name);
        }

        const instructions = `
        <p aria-label="${titleText}">${titleText}</p>
        <p aria-label="${instructionsText}">${instructionsText}</p>`;

        setupAppointmentTimeSection(instructions);
    }

    function setupAppointmentTimeSection(instructions){

        setIsLoading(true);

        getNextAvailableDate().then((nextAvailableDate) => {
            aptTimeComponent.setInstructions(instructions);
            aptTimeComponent.setupCalendarComponent(nextAvailableDate,  analyticsService);
            setIsLoading(false);
            return chooseAppointmentDate(nextAvailableDate);
        });
    }

    async function getNextAvailableDate() {
        const nextSlot = await availabilitySlotService.getNextAvailableDate(configService.sitename, appointment.appointmentTypeId, appointment.locationId, new Date().getTimezoneOffset());
        return nextSlot?.availableDate ?? null;
    }

    function handlePreSelectedLocation() {

        if (presetOptions?.locationId) {

            if (presetOptions.locationId === -1) {
                return true; //virtual/online appointment
            }

            const selectedLocation = locationService.findLocationById(presetOptions.locationId);

            if (selectedLocation) {
                handleLocationSelected(selectedLocation);
                return true;
            }

            console.warn(`Unable to match a location with the preselected location id: ${presetOptions?.locationId}`);

        }

        return false;
    }

    function renderLocations(locations) {

        aptLocationComponent.setInstructions("");
        aptLocationComponent.setLocationData([]);

        if (locations?.length > 0) {

            let titleText = resourcesService.getResource("SELFSCHEDULER_LOCATIONPAGETITLE", DICT_DEFAULT_RESOURCE_TEXT.SELFSCHEDULER_LOCATIONPAGETITLE);
            const selectedName = selectedCategory ? selectedCategory.name : selectedAppointmentType?.name;            
            titleText = titleText.replace("{0}", resourcesService.getFormattedResource(selectedName, 'SELFSCHEDULER_APPOINTMENT_TYPE', '', selectedName));

            aptLocationComponent.setInstructions(`<p aria-label="${titleText}">${titleText}</p>`);
            aptLocationComponent.setLocationData(locations);

            return true;
        }

       return false;
    }

    function submitTimeSlot() {

         if (!selectedAppointmentType) {
            return;
        }

        registrationComponent = container.querySelector("vee24-scheduler-appointment-registration-section");
        registrationComponent.init(resourcesService);
        registrationComponent.setupFormComponent(selectedAppointmentType?.customerInfo);
    }

    async function submitAppointmentForm() {

        if (!selectedAppointmentType) {
            return false;
        }

        const formData = container.querySelectorAll(
            ".v24-scheduler-customerinfo-input"
        );
        const customerInfoStructure = JSON.parse(selectedAppointmentType?.customerInfo);
        aptValidationErrorComponent = container.querySelector("vee24-scheduler-appointment-form-error-section");
        aptValidationErrorComponent.init(resourcesService);
        if (
            isDefinedAndNotNull(customerInfoStructure) &&
            aptValidationErrorComponent.validateForm(formData, customerInfoStructure)
        ) {
            setIsLoading(true);
            const customerInfo = {};
            customerInfoStructure.forEach((field, index) => {
                const id = "v24SchedulerElement-" + index;
                const element = container.querySelector(`#${id}`);

                if (isDefinedAndNotNull(element)) {
                    if (field.type.toLowerCase() === "checkbox") {
                        if (field.mappedField === "SMSConsent" && getFieldByMappedField(formData,"SMSConsent") != null) {
                            customerInfo["SMSConsent"] = element.checked;
                        } else {
                            customerInfo[field.name] = element.checked;
                        }
                    }
                    //if its a fancy intl tel field
                    else if (field.type === "tel" && isCharSetUTF8()) {
                        customerInfo[field.name] = appointment.phoneNumber = window.intlTelInputGlobals
                            .getInstance(element)
                            .getNumber(intlTelInputUtils.numberFormat.E164);
                    } else {
                        customerInfo[field.name] = element.value;
                    }
                }
            });

            const emailField = getEmailField(formData);
            const phoneField = getTelField(formData);

            appointment.phoneNumber = appointment.phoneNumber?.length > 0 ? appointment.phoneNumber : phoneField.value;
            appointment.email = emailField ? emailField.value : ""; //There should only be 1 email field in a form, if for some reason we need to support more then we are going to have to change this
            appointment.customerInfo = JSON.stringify(customerInfo);
            appointment.sitename = configService.sitename;
            appointment.culture = configService.culture;
            appointment.siteSection = configService.siteSection;
            appointment.userguid = userguid;
            appointment.referrer = window.location.href;
            appointment.locationId = appointment.locationId || -1;

            if (appointment.email === "") {
                console.error("The email is not the correct type, please change the appointment form structure");
                //typeError is returned so we go to confirmation page to show this error
                return APPOINTMENT_FORM_SUBMIT_ERRORS.typeError;
            }

            if (phoneField?.required === '1' && appointment.phoneNumber === '') {
                console.error("The telephone is not the correct type, please change the appointment form structure");
                //typeError is returned so we go to confirmation page to show this error
                return APPOINTMENT_FORM_SUBMIT_ERRORS.typeError;
            }

            const response = await aptService.submitAppointment(appointment);
            setIsLoading(false);
            analyticsService.send(EventTypes.Success, EventValues.FormSubmit, getCurrPanel())
            //0 is returned on success meaning we show the success message on the confirmation page
            return response.status;
        } else {
            //validationError is returned so we stay on the form page to show those errors
            aptValidationErrorComponent.focus();
            return APPOINTMENT_FORM_SUBMIT_ERRORS.validationError;
        }
    }

    async function chooseAppointmentDate(selectedDate) {
        if (!selectedDate) {
            console.error('No selected date defined');
            return;
        }
        resetAppointmentTimeSlotSelectionPage();
        //Get Operator TimeSlots Based on selected Date and selectedAppointmentTypeId
        setIsLoading(true);

        appointment.clientOffset = new Date(selectedDate).getTimezoneOffset();
        appointment.appointmentDate = selectedDate;

        const loaded = await availabilitySlotService.getTimeSlots(configService.sitename, appointment.appointmentDate, appointment.appointmentTypeId, appointment.locationId, appointment.clientOffset)

        if (loaded) {
            initOperatorTimeSlotLoaded(availabilitySlotService.timeSlots)
        } else {
            console.error(`Failed to load appointment timeslots`);
        }

    }

    function initOperatorTimeSlotLoaded(operatorTimeSlots) {
        aptTimeComponent.setupTimeslotComponent(operatorTimeSlots, configService.culture, configService.use24hourClock, appointment.appointmentDate);
        setIsLoading(false);
    }

    function showAcknowledgement(responseCode) {

       breadcrumb()?.hide();
       navigation()?.hide();
       confirmationComponent = container.querySelector("vee24-scheduler-appointment-confirmation-section");
       confirmationComponent.init(resourcesService);
       const startTime = appointment.appointmentTime.split("-")[0];
       const formattedAcknowledgementTime = formatTimeSlot(appointment.appointmentDate, startTime, "", configService.culture, configService.use24hourClock)
       confirmationComponent.showAcknowledgement(responseCode, formatAcknowledgmentDate(appointment.appointmentDate), formattedAcknowledgementTime);
       const selectedLocation = locationService.findLocationById(appointment.locationId);
       if (selectedLocation && responseCode == 0) {
            confirmationComponent.showLocationConfirmation(selectedLocation);
        }
        confirmationComponent.focus();
        analyticsService.send(EventTypes.Success, EventValues.AppointmentCreated, getCurrPanel(), {appointment});
    }

    return {
        load, // Scheduler api call from customer page or vee24 api.
        chooseAppointmentDate, // Scheduler api call from vanilla calendar.
        initOperatorTimeSlotLoaded, //scheduler api call from service
        showAcknowledgement, //scheduler api call from service
        discardDateSelectionOnChange: resetAppointmentTimeSlotSelectionPage,
        appointmentTypes: null,
        resources: null,
        restAPI: "api.vee24.com",
        serviceRegion: "web.vee24.com",
        staticDomain: "static.vee24.com",
        site: "",
        siteSection:  "",
        culture:  "",
        use24hourClock: '0',
        sessionIdentifier: '',
        panelIdentifier: '',
        showHierarchicalTreeView: false
    };
});

if (!window.v24Scheduler) {
    window.v24Scheduler = new v24SelfScheduler();
} else {
    console.warn("scheduler is already on page");
}

//TODO: FIX this crazy logic
if (isDefinedAndNotNull(window.vee24) && vee24.autoInitializeScheduler) {
    v24Scheduler.load();
}
